/**
 * Reading the assistant's answers aloud with Gemini TTS (design: read
 * pieces of a streaming answer as they complete, so speaking starts long
 * before the whole answer is written; replay a stored answer on request).
 * Adapted from the MEIS-13490 prototype's `agentSpeechOutput.ts` to the
 * product's own wire contract:
 *  - a still-streaming answer: `POST /api/agent/speech { text, conversationId }`
 *    → NDJSON `audio`/`usage` lines (`agentSpeechStream.ts`'s
 *    `readAgentSpeechStream`); every piece's `usage` token is booked once
 *    the answer's stored `messageId` is known, via the `bookAgentSpeechUsage`
 *    mutation (MEIS-13516, P1 — replaces `POST /api/agent/speech/book`; the
 *    audio itself stays HTTP, see `agentSpeechOutput.graphql`).
 *  - a stored answer (replay, or a message read for the first time after
 *    the fact): `POST /api/agent/speech { messageId }` → raw 16-bit PCM
 *    (`content-type: audio/l16`, `x-audio-sample-rate`), booked server-side.
 *
 * Ported from commit fba1d661c2 ("control reading aloud from each answer's
 * speaker instead of the header"): there is no separate speaker control —
 * each message's own "Read aloud" action (in `AgentChatBox.tsx`) IS the
 * control, identified by the chat message's own id
 * (`ChatMessageActionsResolveContext.messageId`) it was opened with; once the
 * answer's backend-assigned `messageId` is known (a stored replay, or the end
 * of a live reading), that id names the same reading too (`isActiveKey`,
 * `matchesSpeechKey`) — the two differ for an answer that was still
 * streaming, and the chat lists it under the stored one after a reload. Playback
 * is a small state machine (`agentSpeechPlayback`): idle → loading → playing
 * ⇄ paused → ended, with pause/resume via `AudioContext.suspend`/`resume` and
 * a replay from the kept PCM samples (no new request, no new cost) when the
 * same message is started again right after it ended. A pause holds the
 * whole reading: pieces still arriving are scheduled into the suspended
 * context silently and only heard after resume, at the same piece and offset
 * (`openContext` never resumes the context while paused).
 *
 * Only the shown answer is read: each requested piece keeps its own
 * request and audio, so when the shown text stops extending what was read
 * (a withdrawn draft, or the finished reply replacing it), every piece the
 * shown text no longer reads the same is cancelled and silenced (`reconcile`)
 * and reading continues from there. Whatever happens to the last piece —
 * played, failed, or nothing left to request — `finishLiveStream` settles
 * the session after it.
 *
 * Pure logic (which text is ready to speak, the playback state machine,
 * cost math, booking) lives in `agentSpeechPieces.ts`/`agentSpeechPlayback.ts`/
 * `agentSpeechCost.ts`/`agentSpeechBooking.ts` and is unit-tested there; this
 * module is the Web Audio + fetch wrapper around them.
 * `agentSpeechOutput.spec.ts` covers its async races (`finishSession` racing
 * a pending schedule, `finishLiveStream` racing its queued piece's usage
 * token), pause/resume/stop across the queue, a failing last piece and a
 * withdrawn draft — using a stubbed `AudioContext`/`AudioBufferSourceNode`/
 * `AudioBuffer` and a mocked `fetch`.
 */
import { useApolloClient } from "@apollo/client";
import React from "react";
import { useAuth } from "../../auth/useAuth.js";
import { useSelectedUser } from "../../auth/useSelectedUser.js";
import { SELECTED_USER_HTTP_HEADER } from "../../constants.js";
import {
  collectSpeechUsage,
  type SpeechUsageToken,
} from "./agentSpeechBooking.js";
import { BookAgentSpeechUsageDocument } from "./agentSpeechOutput.generated.js";
import {
  keptSpeechPieces,
  type SpeechPieceCut,
  splitSpeechPieces,
} from "./agentSpeechPieces.js";
import {
  canReplayFromKept,
  matchesSpeechKey,
  nextSpeechPlaybackState,
  type SpeechPlaybackEventType,
  type SpeechPlaybackState,
} from "./agentSpeechPlayback.js";
import {
  AgentSpeechStreamError,
  base64ToUint8Array,
  pcm16ToFloat32,
  readAgentSpeechStream,
} from "./agentSpeechStream.js";

/**
 * One piece of text sent to `/api/agent/speech` (the whole answer, for a
 * stored replay) and the audio it produced so far — kept per piece so a piece
 * whose text was withdrawn can be silenced and cancelled on its own.
 */
type SpeechPiece = {
  /** The raw Markdown sent for this piece. */
  text: string;
  /** Aborts this piece's own request. */
  controller: AbortController;
  sources: AudioBufferSourceNode[];
  buffers: AudioBuffer[];
  /** Context time this piece's last scheduled audio ends (0: nothing scheduled yet). */
  endAt: number;
  /** Withdrawn (its text is no longer shown): never scheduled again, its audio stopped. */
  dropped: boolean;
};

/** One in-progress or finished reading (a whole message, or a still-streaming one). */
type StreamSession = {
  /** How much of the growing text has already been turned into pieces (live sessions only). */
  consumed: number;
  lastFullText: string;
  /** Serialises piece requests: one at a time, in order. */
  chain: Promise<void>;
  /** Serialises sample schedules, so chunks land in the order they arrived even across the async context hop. */
  scheduleChain: Promise<void>;
  /** The pieces requested so far that are still part of the shown text (plus dropped ones until they are cleaned up). */
  pieces: SpeechPiece[];
  /** The text stream itself is finished (the rest was flushed); only playback continues. */
  done: boolean;
  /** Usage tokens collected while reading a live session's pieces, not yet booked. */
  pendingUsage: SpeechUsageToken[];
  /**
   * Incremented in `scheduleSamples` before its async `openContext()` hop,
   * decremented once that hop lands — lets `finishSession` wait for every
   * already-queued piece's schedule to resolve (and so the last source to
   * settle on the real last one) before deciding the session is done.
   */
  pendingSchedules: number;
  /** `finishSession` ran while a schedule was still pending; the schedule that brings `pendingSchedules` to 0 completes the session instead. */
  awaitingFinish: boolean;
  /** Set by `playStored`: which stored answer this session replays, so its completion can tell `onStoredReplayFinished` apart from a live session's. */
  storedMessageId: string | null;
  /**
   * The shown text stopped being an extension of what was fed (a withdrawn
   * draft): pieces are reconciled against the text shown once this short
   * timer fires, so the transient empty text between a discarded draft and
   * the finished reply replacing it does not throw away pieces the reply
   * still reads the same.
   */
  reconcileTimer: ReturnType<typeof setTimeout> | null;
  /** The latest text fed while `reconcileTimer` is pending. */
  pendingText: string;
  /** Some audio of this session was scheduled to play — also of a piece withdrawn since. */
  heardAudio: boolean;
};

/**
 * How long a divergent text (a withdrawn draft) waits before its pieces are
 * reconciled: long enough to see the finished reply that replaces the draft
 * in the same stream (a few ~16 ms chat flushes), short enough that a
 * withdrawn draft's audio stops right away.
 */
const RECONCILE_DELAY_MS = 120;
/** Torn-down readings whose usage still awaits `settleUsage`; older ones are dropped. */
const MAX_ORPHANED_USAGE = 10;

/** The live pieces' last scheduled source — the one whose end ends the session. */
function lastLiveSource(
  forSession: StreamSession
): AudioBufferSourceNode | null {
  for (let i = forSession.pieces.length - 1; i >= 0; i--) {
    const piece = forSession.pieces[i];
    if (piece.dropped) continue;
    const source = piece.sources[piece.sources.length - 1];
    if (source) return source;
  }
  return null;
}

/** Silences one piece: cancels its request and stops every source it scheduled. */
function dropPiece(piece: SpeechPiece) {
  piece.dropped = true;
  piece.controller.abort();
  for (const source of piece.sources) {
    try {
      source.stop();
    } catch {
      // Not started yet or already ended.
    }
  }
}

export type SpeechOutput = {
  /** Whether the backend can speak (a Gemini key is configured) — every speaker action is hidden entirely when false. */
  available: boolean;
  state: SpeechPlaybackState;
  /**
   * Whether `key` names the message currently (or last) read: the chat
   * message id (`ChatMessageActionsResolveContext.messageId`) its reading
   * was opened with, or the stored answer id learned for it since
   * (`matchesSpeechKey`). False before anything played.
   */
  isActiveKey: (key: string) => boolean;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  /**
   * Starts reading `key` (a stored answer when `messageId` is given and
   * `streaming` is false, otherwise an incremental live reading) from the
   * top, replacing whatever plays — unless `key` is the very message that
   * just finished and its audio is still kept, which replays that instead
   * (no new request, no new cost).
   */
  readAloud: (
    key: string,
    text: string,
    messageId: string | null,
    streaming: boolean
  ) => void;
  /** Starts a fresh incremental reading of a still-streaming message, replacing whatever plays. */
  beginLiveStream: (key: string) => void;
  /** Feeds the active streaming message's grown text; call repeatedly as it grows. */
  continueLiveStream: (text: string) => void;
  /** The active streaming message finished: flushes the rest and books every collected usage token onto its stored id. */
  finishLiveStream: (text: string, messageId: string | null) => void;
  /**
   * The message `key` finished while it was no longer the active reading
   * (stopped or replaced mid-answer): books the usage of the pieces already
   * read onto its stored id. A no-op without such leftovers.
   */
  settleUsage: (key: string, messageId: string | null) => void;
};

export function useAgentSpeech(opts: {
  available: boolean;
  /** The open conversation's id, read fresh on every request (a live session may start before the first reply assigns one). */
  conversationId: () => string | null;
  onCost?: (messageId: string, costUsd: number) => void;
  /**
   * A stored answer's replay (`playStored`) finished: unlike the live path,
   * its cost is booked server-side directly onto the message, with no
   * client-side `onCost` call — the caller can use this to refresh that
   * message's cost (e.g. by reopening the conversation).
   */
  onStoredReplayFinished?: (messageId: string) => void;
  /**
   * A reading ended without any of its audio having played — every piece
   * failed or came back empty (the server drops audio far longer than its
   * text) — so the caller can tell the user instead of leaving them with
   * silence. Not called for a reading the user stopped.
   */
  onNothingHeard?: () => void;
}): SpeechOutput {
  const { auth } = useAuth();
  const { selectedUserId } = useSelectedUser();
  const apolloClient = useApolloClient();
  const latest = React.useRef(opts);
  latest.current = opts;

  const [state, setState] = React.useState<SpeechPlaybackState>("idle");
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const [activeKey, setActiveKeyState] = React.useState<string | null>(null);
  const activeKeyRef = React.useRef<string | null>(null);
  // The stored answer id of the active reading, once known — an alias of
  // `activeKey` (see `matchesSpeechKey`); reset with every new reading.
  const [activeKeyAlias, setActiveKeyAlias] = React.useState<string | null>(
    null
  );
  const activeKeyAliasRef = React.useRef<string | null>(null);
  const setActiveKey = React.useCallback((key: string | null) => {
    activeKeyRef.current = key;
    setActiveKeyState(key);
    activeKeyAliasRef.current = null;
    setActiveKeyAlias(null);
  }, []);
  /** Learns the active reading's stored answer id as an alias of its key. */
  const learnStoredId = React.useCallback((messageId: string | null) => {
    if (!messageId || messageId === activeKeyRef.current) return;
    activeKeyAliasRef.current = messageId;
    setActiveKeyAlias(messageId);
  }, []);
  const isActiveKey = React.useCallback(
    (key: string) => matchesSpeechKey(key, activeKey, activeKeyAlias),
    [activeKey, activeKeyAlias]
  );
  const dispatch = React.useCallback((event: SpeechPlaybackEventType) => {
    setState(current => nextSpeechPlaybackState(current, event));
  }, []);

  const audio = React.useRef<AudioContext | null>(null);
  /** Sources of a kept-samples replay (`replayKept`), which has no pieces of its own. */
  const replaySources = React.useRef<AudioBufferSourceNode[]>([]);
  const keptBuffers = React.useRef<AudioBuffer[]>([]);
  const nextStart = React.useRef(0);
  const session = React.useRef<StreamSession | null>(null);
  /** Usage of torn-down live readings, not yet booked (their answer was still streaming); see `settleUsage`. */
  const orphanedUsage = React.useRef<
    Array<{ key: string; alias: string | null; tokens: SpeechUsageToken[] }>
  >([]);
  /**
   * Set synchronously by `pause` (the state itself only updates on the next
   * render): while true, nothing may resume the suspended context — audio
   * arriving meanwhile is scheduled silently and only heard after `resume`.
   */
  const paused = React.useRef(false);

  const headers = React.useCallback(async () => {
    await auth.updateToken(5);
    return {
      ...(auth.token ? { authorization: `Bearer ${auth.token}` } : {}),
      ...(selectedUserId
        ? { [SELECTED_USER_HTTP_HEADER]: selectedUserId }
        : {}),
    };
  }, [auth, selectedUserId]);

  /**
   * The audio context, created on first use. Resumed (browsers start it
   * suspended until a user gesture) — except while paused: a piece arriving
   * during a pause used to resume the context here and so un-pause the whole
   * reading.
   */
  const openContext = React.useCallback(async () => {
    const context =
      audio.current && audio.current.state !== "closed"
        ? audio.current
        : new AudioContext();
    audio.current = context;
    if (!paused.current) await context.resume();
    return context;
  }, []);

  /** Tears down everything currently scheduled/kept; leaves state/key to the caller. */
  const teardownPlayback = React.useCallback(() => {
    const current = session.current;
    session.current = null;
    if (current) {
      if (current.reconcileTimer !== null) clearTimeout(current.reconcileTimer);
      // A finished session's pending finish chain books its own tokens.
      if (
        !current.done &&
        activeKeyRef.current &&
        current.pendingUsage.length > 0
      )
        orphanedUsage.current = [
          ...orphanedUsage.current,
          {
            key: activeKeyRef.current,
            alias: activeKeyAliasRef.current,
            tokens: current.pendingUsage.splice(0),
          },
        ].slice(-MAX_ORPHANED_USAGE);
      current.pieces.forEach(dropPiece);
    }
    for (const source of replaySources.current) {
      try {
        source.stop();
      } catch {
        // Not started yet or already ended.
      }
    }
    replaySources.current = [];
    keptBuffers.current = [];
    nextStart.current = 0;
    paused.current = false;
  }, []);

  const stop = React.useCallback(() => {
    teardownPlayback();
    setActiveKey(null);
    dispatch("stop");
  }, [dispatch, setActiveKey, teardownPlayback]);

  /**
   * The session's last piece of scheduled audio actually ended (or there was
   * none): dispatches "finished" and, for a stored-answer replay, tells
   * `onStoredReplayFinished` so its caller can refresh that message's cost.
   */
  const completeSession = React.useCallback(
    (forSession: StreamSession) => {
      const finish = () => {
        if (session.current !== forSession) return;
        keptBuffers.current = forSession.pieces
          .filter(piece => !piece.dropped)
          .flatMap(piece => piece.buffers);
        dispatch("finished");
        if (forSession.pieces.length > 0 && !forSession.heardAudio)
          latest.current.onNothingHeard?.();
        if (forSession.storedMessageId)
          latest.current.onStoredReplayFinished?.(forSession.storedMessageId);
      };
      const lastSource = lastLiveSource(forSession);
      // Nothing left to wait for when the audio already ended (e.g. the final
      // piece failed after the earlier ones finished): its `onended` is past.
      const stillAudible =
        audio.current !== null && audio.current.currentTime < nextStart.current;
      if (lastSource && stillAudible)
        lastSource.onended = () => {
          // A stopped (dropped) source fires "ended" too — only the real last one counts.
          if (lastLiveSource(forSession) === lastSource) finish();
        };
      else finish();
    },
    [dispatch]
  );

  /**
   * Schedules one decoded chunk of `piece` right after whatever is already
   * queued; the first chunk of a session flips the state to "playing". Never
   * schedules for a torn-down session or a dropped piece.
   */
  const scheduleSamples = React.useCallback(
    (
      samples: Float32Array,
      rate: number,
      forSession: StreamSession,
      piece: SpeechPiece
    ) => {
      if (samples.length === 0 || session.current !== forSession) return;
      if (piece.dropped) return;
      forSession.pendingSchedules += 1;
      forSession.scheduleChain = forSession.scheduleChain.then(async () => {
        try {
          const context = await openContext();
          if (session.current !== forSession || piece.dropped) return;
          const buffer = context.createBuffer(1, samples.length, rate);
          buffer.getChannelData(0).set(samples);
          const source = context.createBufferSource();
          source.buffer = buffer;
          source.connect(context.destination);
          const start = Math.max(nextStart.current, context.currentTime + 0.05);
          source.start(start);
          nextStart.current = start + buffer.duration;
          piece.sources.push(source);
          piece.buffers.push(buffer);
          piece.endAt = nextStart.current;
          forSession.heardAudio = true;
          if (stateRef.current !== "playing") dispatch("first-audio");
        } catch {
          // No audio context: this chunk stays silent.
        } finally {
          forSession.pendingSchedules -= 1;
          if (forSession.pendingSchedules === 0 && forSession.awaitingFinish) {
            forSession.awaitingFinish = false;
            completeSession(forSession);
          }
        }
      });
    },
    [completeSession, dispatch, openContext]
  );

  /**
   * Once every piece of the final chunk is at least queued, ends the session
   * — deferring to the schedule(s) still pending (their own async
   * `openContext()` hop) when this runs before they land, so the last source
   * is the real last one, not `null`/stale.
   */
  const finishSession = React.useCallback(
    (forSession: StreamSession) => {
      if (session.current !== forSession) return;
      if (forSession.pendingSchedules > 0) {
        forSession.awaitingFinish = true;
        return;
      }
      completeSession(forSession);
    },
    [completeSession]
  );

  const bookUsage = React.useCallback(
    async (messageId: string, tokens: SpeechUsageToken[]) => {
      for (const usage of tokens) {
        try {
          const { data } = await apolloClient.mutate({
            mutation: BookAgentSpeechUsageDocument,
            variables: { messageId, token: usage.token },
            // Best-effort accounting: a failure is swallowed below, so the
            // global error link must not toast it either.
            context: { noErrorNotification: true },
          });
          if (data?.bookAgentSpeechUsage.booked)
            latest.current.onCost?.(
              messageId,
              data.bookAgentSpeechUsage.costUsd
            );
        } catch {
          // Booking is best-effort; the answer's own stored cost still lands eventually.
        }
      }
    },
    [apolloClient]
  );

  /**
   * Requests and schedules one piece. Never throws: a failed piece (network
   * error, error line, a stream without its usage line) is skipped and the
   * rest still plays — the session's end is settled by the step
   * `finishLiveStream` chains after the last piece, whatever happened here.
   */
  const sendPiece = React.useCallback(
    async (forSession: StreamSession, piece: SpeechPiece) => {
      if (piece.controller.signal.aborted) return;
      try {
        const response = await fetch("/api/agent/speech", {
          method: "POST",
          signal: piece.controller.signal,
          headers: {
            "content-type": "application/json",
            ...(await headers()),
          },
          body: JSON.stringify({
            text: piece.text,
            conversationId: latest.current.conversationId(),
          }),
        });
        if (!response.ok || !response.body)
          throw new Error(`speech ${response.status}`);
        let carry: number | null = null;
        const usage = await readAgentSpeechStream(
          response.body.getReader(),
          event => {
            if (piece.dropped) return;
            const bytes = base64ToUint8Array(event.pcm);
            const converted = pcm16ToFloat32(bytes, carry);
            carry = converted.carry;
            scheduleSamples(
              converted.samples,
              event.sampleRate,
              forSession,
              piece
            );
          }
        );
        forSession.pendingUsage = collectSpeechUsage(
          forSession.pendingUsage,
          usage
        );
      } catch (error) {
        // This piece failed or was withdrawn: the rest (already queued) still
        // plays. An error line's trailing usage (the partial cost) is still booked.
        if (error instanceof AgentSpeechStreamError && error.usage)
          forSession.pendingUsage = collectSpeechUsage(
            forSession.pendingUsage,
            error.usage
          );
      }
    },
    [headers, scheduleSamples]
  );

  /** Queues `cuts` as new pieces, one request at a time after the ones already queued. */
  const queuePieces = React.useCallback(
    (forSession: StreamSession, cuts: SpeechPieceCut[]) => {
      for (const cut of cuts) {
        const piece: SpeechPiece = {
          text: cut.piece,
          controller: new AbortController(),
          sources: [],
          buffers: [],
          endAt: 0,
          dropped: false,
        };
        forSession.pieces.push(piece);
        forSession.consumed = cut.end;
        forSession.chain = forSession.chain.then(() =>
          sendPiece(forSession, piece)
        );
      }
    },
    [sendPiece]
  );

  /**
   * The shown text is no longer an extension of what was read (a draft was
   * withdrawn, or the finished reply replaced it): keeps the leading pieces
   * `text` still reads the same (`speechComparableText`), silences and
   * cancels every other one — queued, requested or playing — and continues
   * right after the last piece kept.
   */
  const reconcile = React.useCallback(
    (forSession: StreamSession, text: string) => {
      const live = forSession.pieces.filter(piece => !piece.dropped);
      const { kept, consumed } = keptSpeechPieces(
        live.map(piece => piece.text),
        text
      );
      live.slice(kept).forEach(dropPiece);
      forSession.pieces = live.slice(0, kept);
      forSession.consumed = consumed;
      forSession.lastFullText = text;
      nextStart.current = Math.max(
        0,
        ...forSession.pieces.map(piece => piece.endAt)
      );
    },
    []
  );

  /** Extracts the pieces newly ready in `text` (or, `final`, everything left) and queues them. */
  const extract = React.useCallback(
    (forSession: StreamSession, text: string, final: boolean) => {
      forSession.lastFullText = text;
      queuePieces(
        forSession,
        splitSpeechPieces(text, forSession.consumed, final)
      );
    },
    [queuePieces]
  );

  /**
   * Feeds `text` (the message's whole shown text) into the current live
   * session. When `text` is not a plain extension of what was fed before (a
   * withdrawn draft, or the finished reply replacing the streamed one), the
   * pieces are reconciled against the shown text shortly after
   * (`RECONCILE_DELAY_MS`), or at once for the `final` text — nothing read
   * from a withdrawn draft keeps playing or is still requested.
   */
  const feed = React.useCallback(
    (text: string, final: boolean) => {
      const current = session.current;
      if (!current) return;
      const diverged =
        current.reconcileTimer !== null ||
        !text.startsWith(current.lastFullText);
      if (!diverged) {
        extract(current, text, final);
        return;
      }
      current.pendingText = text;
      if (final) {
        if (current.reconcileTimer !== null)
          clearTimeout(current.reconcileTimer);
        current.reconcileTimer = null;
        reconcile(current, text);
        extract(current, text, true);
        return;
      }
      if (current.reconcileTimer !== null) return;
      current.reconcileTimer = setTimeout(() => {
        current.reconcileTimer = null;
        if (session.current !== current) return;
        reconcile(current, current.pendingText);
        extract(current, current.pendingText, false);
      }, RECONCILE_DELAY_MS);
    },
    [extract, reconcile]
  );

  const newSession = (): StreamSession => ({
    consumed: 0,
    lastFullText: "",
    chain: Promise.resolve(),
    scheduleChain: Promise.resolve(),
    pieces: [],
    done: false,
    pendingUsage: [],
    pendingSchedules: 0,
    awaitingFinish: false,
    storedMessageId: null,
    reconcileTimer: null,
    pendingText: "",
    heardAudio: false,
  });

  /** Tears down whatever played before and opens a fresh session for `key`. */
  const openSession = React.useCallback(
    (key: string) => {
      teardownPlayback();
      setActiveKey(key);
      dispatch("speak");
      session.current = newSession();
    },
    [dispatch, setActiveKey, teardownPlayback]
  );

  const beginLiveStream = React.useCallback(
    (key: string) => openSession(key),
    [openSession]
  );

  const continueLiveStream = React.useCallback(
    (text: string) => feed(text, false),
    [feed]
  );

  const finishLiveStream = React.useCallback(
    (text: string, messageId: string | null) => {
      const current = session.current;
      if (!current) return;
      learnStoredId(messageId);
      feed(text, true);
      current.done = true;
      // Settles the session after the last queued piece, whether it played,
      // failed (network error, no usage line) or there was no piece left to
      // request at all — never left "playing" with nothing to end it.
      // `pendingUsage` for the piece(s) just queued above is only populated
      // once each `sendPiece` call chained onto `current.chain` resolves —
      // read/splice the list only after the chain settles, skipping the
      // booking if it turned out empty.
      current.chain = current.chain.then(() => {
        finishSession(current);
        if (!messageId) return;
        const tokens = current.pendingUsage.splice(0);
        if (tokens.length > 0) void bookUsage(messageId, tokens);
      });
    },
    [bookUsage, feed, finishSession, learnStoredId]
  );

  const settleUsage = React.useCallback(
    (key: string, messageId: string | null) => {
      const mine = orphanedUsage.current.filter(entry =>
        matchesSpeechKey(key, entry.key, entry.alias)
      );
      if (mine.length === 0) return;
      orphanedUsage.current = orphanedUsage.current.filter(
        entry => !mine.includes(entry)
      );
      if (!messageId) return;
      const tokens = mine.flatMap(entry => entry.tokens);
      if (tokens.length > 0) void bookUsage(messageId, tokens);
    },
    [bookUsage]
  );

  /** Replays the kept samples of the session that just ended: no new request, no new cost. */
  const replayKept = React.useCallback(() => {
    const context = audio.current;
    if (!context || keptBuffers.current.length === 0) return;
    for (const source of replaySources.current) {
      try {
        source.stop();
      } catch {
        // Already ended.
      }
    }
    replaySources.current = [];
    paused.current = false;
    void context.resume();
    nextStart.current = context.currentTime + 0.05;
    let lastSource: AudioBufferSourceNode | null = null;
    for (const buffer of keptBuffers.current) {
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(context.destination);
      const start = Math.max(nextStart.current, context.currentTime + 0.05);
      source.start(start);
      nextStart.current = start + buffer.duration;
      replaySources.current.push(source);
      lastSource = source;
    }
    const replayed = lastSource;
    if (replayed)
      replayed.onended = () => {
        if (replaySources.current.includes(replayed)) dispatch("finished");
      };
    dispatch("replay");
  }, [dispatch]);

  const playStored = React.useCallback(
    (key: string, messageId: string) => {
      openSession(key);
      learnStoredId(messageId);
      const forSession = session.current;
      if (!forSession) return;
      forSession.done = true;
      forSession.storedMessageId = messageId;
      const piece: SpeechPiece = {
        text: "",
        controller: new AbortController(),
        sources: [],
        buffers: [],
        endAt: 0,
        dropped: false,
      };
      forSession.pieces.push(piece);
      const controller = piece.controller;
      void (async () => {
        try {
          const response = await fetch("/api/agent/speech", {
            method: "POST",
            signal: controller.signal,
            headers: {
              "content-type": "application/json",
              ...(await headers()),
            },
            body: JSON.stringify({ messageId }),
          });
          if (!response.ok || !response.body)
            throw new Error(`speech ${response.status}`);
          const rate =
            Number(response.headers.get("x-audio-sample-rate")) || 24000;
          const reader = response.body.getReader();
          let carry: number | null = null;
          for (;;) {
            const { done, value } = await reader.read();
            if (controller.signal.aborted) return;
            if (value) {
              const converted = pcm16ToFloat32(value, carry);
              carry = converted.carry;
              scheduleSamples(converted.samples, rate, forSession, piece);
            }
            if (done) break;
          }
          finishSession(forSession);
        } catch {
          // A failed replay is settled, never left loading/playing: whatever
          // audio did arrive plays out and ends the session; with none, it
          // stops at once.
          if (controller.signal.aborted || session.current !== forSession)
            return;
          if (piece.sources.length > 0 || forSession.pendingSchedules > 0)
            finishSession(forSession);
          else {
            dispatch("stop");
            latest.current.onNothingHeard?.();
          }
        }
      })();
    },
    [
      dispatch,
      finishSession,
      headers,
      learnStoredId,
      openSession,
      scheduleSamples,
    ]
  );

  const readAloud = React.useCallback(
    (
      key: string,
      text: string,
      messageId: string | null,
      streaming: boolean
    ) => {
      if (
        canReplayFromKept(
          matchesSpeechKey(
            key,
            activeKeyRef.current,
            activeKeyAliasRef.current
          ),
          stateRef.current,
          keptBuffers.current.length > 0
        )
      ) {
        replayKept();
        return;
      }
      if (streaming) {
        beginLiveStream(key);
        continueLiveStream(text);
      } else if (messageId) {
        playStored(key, messageId);
      }
    },
    [beginLiveStream, continueLiveStream, playStored, replayKept]
  );

  /**
   * Pauses the whole reading, not just the audio currently heard: the context
   * is suspended, and pieces still arriving are scheduled into it silently
   * (`openContext` no longer resumes it) — nothing plays until `resume`,
   * which continues at the same piece and offset.
   */
  const pause = React.useCallback(() => {
    if (stateRef.current !== "playing") return;
    paused.current = true;
    void audio.current?.suspend();
    dispatch("pause");
  }, [dispatch]);

  const resume = React.useCallback(() => {
    if (stateRef.current !== "paused") return;
    paused.current = false;
    void audio.current?.resume();
    dispatch("resume");
  }, [dispatch]);

  React.useEffect(() => stop, [stop]);

  return {
    available: opts.available,
    state,
    isActiveKey,
    pause,
    resume,
    stop,
    readAloud,
    beginLiveStream,
    continueLiveStream,
    finishLiveStream,
    settleUsage,
  };
}
